import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { SuperAdminService, type RecurringBillAnchor } from '../../services/superAdminService';
import { convertDollarsToCad, formatCad, type ExchangeRatesToCad } from '../../lib/currencyToCad';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const TZ = 'America/Vancouver';

function vancouverParts(iso: string): { y: number; m: number; d: number } | null {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(t));
  const num = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const y = num('year');
  const m = num('month');
  const d = num('day');
  if (!y || !m || !d) return null;
  return { y, m, d };
}

function keyOf(y: number, m: number, d: number): number {
  return y * 10000 + m * 100 + d;
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function shiftDays(p: { y: number; m: number; d: number }, delta: number): { y: number; m: number; d: number } {
  const dt = new Date(Date.UTC(p.y, p.m - 1, p.d, 19, 0, 0));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

/** Invoice charge for one renewal. amountMonthly is the monthly equivalent, in dollars. */
function chargeDollars(amountMonthly: number, interval: string | null): number {
  const unit = (interval || 'month').toLowerCase();
  if (unit === 'year') return amountMonthly * 12;
  if (unit === 'week') return (amountMonthly * 12) / 52;
  if (unit === 'day') return (amountMonthly * 12) / 365;
  return amountMonthly;
}

export function projectBillingMonth(
  anchors: RecurringBillAnchor[],
  year: number,
  month: number,
  rates: ExchangeRatesToCad,
): { amounts: number[]; counts: number[] } {
  const days = daysInMonth(year, month);
  const amounts = Array.from({ length: days }, () => 0);
  const counts = Array.from({ length: days }, () => 0);
  const monthStart = keyOf(year, month, 1);
  const monthEnd = keyOf(year, month, days);

  for (const row of anchors) {
    const anchor = vancouverParts(row.anchorIso);
    if (!anchor) continue;
    const amount = convertDollarsToCad(chargeDollars(row.amountMonthly, row.interval), row.currency, rates);
    if (!(amount > 0)) continue;
    const notBefore = row.notBeforeIso ? vancouverParts(row.notBeforeIso) : null;
    const cancel = row.cancelAtIso ? vancouverParts(row.cancelAtIso) : null;
    const notBeforeKey = notBefore ? keyOf(notBefore.y, notBefore.m, notBefore.d) : null;
    const cancelKey = cancel ? keyOf(cancel.y, cancel.m, cancel.d) : null;

    const add = (day: number) => {
      if (day < 1 || day > days) return;
      const k = keyOf(year, month, day);
      if (notBeforeKey != null && k < notBeforeKey) return;
      if (cancelKey != null && k >= cancelKey) return;
      amounts[day - 1] += amount;
      counts[day - 1] += 1;
    };

    const unit = (row.interval || 'month').toLowerCase();
    if (unit === 'year') {
      if (month === anchor.m) add(Math.min(anchor.d, days));
      continue;
    }
    if (unit === 'week' || unit === 'day') {
      const step = unit === 'week' ? 7 : 1;
      let cursor = { ...anchor };
      let guard = 0;
      if (keyOf(cursor.y, cursor.m, cursor.d) < monthStart) {
        while (keyOf(cursor.y, cursor.m, cursor.d) < monthStart && guard < 4000) {
          cursor = shiftDays(cursor, step);
          guard += 1;
        }
      } else {
        while (keyOf(cursor.y, cursor.m, cursor.d) >= monthStart && guard < 4000) {
          cursor = shiftDays(cursor, -step);
          guard += 1;
        }
        cursor = shiftDays(cursor, step);
      }
      guard = 0;
      while (keyOf(cursor.y, cursor.m, cursor.d) <= monthEnd && guard < 40) {
        if (cursor.y === year && cursor.m === month) add(cursor.d);
        cursor = shiftDays(cursor, step);
        guard += 1;
      }
      continue;
    }
    add(Math.min(anchor.d, days));
  }

  return { amounts, counts };
}

function currentVancouverMonth(): { y: number; m: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());
  const num = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { y: num('year'), m: num('month') };
}

function shiftMonth(y: number, m: number, delta: number): { y: number; m: number } {
  const dt = new Date(Date.UTC(y, m - 1 + delta, 1));
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1 };
}

export default function BillingByDayCard({ rates }: { rates: ExchangeRatesToCad }) {
  const [anchors, setAnchors] = useState<RecurringBillAnchor[] | null>(null);
  const [unscheduled, setUnscheduled] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState(currentVancouverMonth);

  useEffect(() => {
    let cancelled = false;
    SuperAdminService.listRecurringBillAnchors()
      .then((res) => {
        if (cancelled) return;
        setAnchors(res.anchors);
        setUnscheduled(res.unscheduled);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load renewals');
        setAnchors([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const today = currentVancouverMonth();
  const onThisMonth = cursor.y === today.y && cursor.m === today.m;
  const projected = useMemo(
    () => (anchors ? projectBillingMonth(anchors, cursor.y, cursor.m, rates) : null),
    [anchors, cursor, rates],
  );

  const monthTotal = projected ? projected.amounts.reduce((sum, n) => sum + n, 0) : 0;
  const renewalCount = projected ? projected.counts.reduce((sum, n) => sum + n, 0) : 0;
  const monthLabel = new Date(Date.UTC(cursor.y, cursor.m - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return (
    <div
      className="bg-white rounded-[14px] p-3.5 mb-2"
      style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Billing by day</p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => setCursor((c) => shiftMonth(c.y, c.m, -1))}
            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-[13px] font-medium text-gray-900 min-w-[8.5rem] text-center tabular-nums">
            {monthLabel}
          </span>
          <button
            type="button"
            aria-label="Next month"
            onClick={() => setCursor((c) => shiftMonth(c.y, c.m, 1))}
            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          {!onThisMonth && (
            <button
              type="button"
              onClick={() => setCursor(currentVancouverMonth())}
              className="ml-1 text-[11px] font-medium text-purple-600 hover:text-purple-700"
            >
              This month
            </button>
          )}
        </div>
      </div>
      <p className="text-[12px] text-gray-400 mb-3">
        {anchors == null
          ? 'Loading renewals…'
          : `${formatCad(monthTotal)} · ${renewalCount} renewal${renewalCount === 1 ? '' : 's'}`}
      </p>
      {error ? (
        <p className="text-[13px] text-red-500 py-6 text-center">{error}</p>
      ) : anchors == null ? (
        <div className="flex items-center justify-center h-[220px]">
          <div className="w-5 h-5 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
        </div>
      ) : (
        <div style={{ height: 220 }}>
          <Bar
            data={{
              labels: projected!.amounts.map((_, i) => String(i + 1)),
              datasets: [
                {
                  data: projected!.amounts,
                  backgroundColor: 'rgba(139, 92, 246, 0.85)',
                  borderRadius: 3,
                  maxBarThickness: 18,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  backgroundColor: 'rgba(17,24,39,0.95)',
                  titleFont: { size: 12 },
                  bodyFont: { size: 12 },
                  padding: 10,
                  cornerRadius: 8,
                  callbacks: {
                    title: (items) => `${monthLabel.split(' ')[0]} ${items[0]?.label ?? ''}`,
                    label: (ctx) => {
                      const n = projected!.counts[ctx.dataIndex] || 0;
                      const dollars = projected!.amounts[ctx.dataIndex] || 0;
                      if (n === 0) return ' No renewals';
                      return ` ${formatCad(dollars)} · ${n} renewal${n === 1 ? '' : 's'}`;
                    },
                  },
                },
              },
              scales: {
                x: {
                  grid: { display: false },
                  ticks: { font: { size: 10 }, color: '#9CA3AF', maxRotation: 0, autoSkip: true, maxTicksLimit: 16 },
                },
                y: {
                  beginAtZero: true,
                  grid: { color: 'rgba(0,0,0,0.04)' },
                  ticks: {
                    font: { size: 10 },
                    color: '#9CA3AF',
                    maxTicksLimit: 5,
                    callback: (value) => {
                      const n = Number(value) || 0;
                      if (n >= 1000) return `$${Math.round(n / 100) / 10}k`;
                      return `$${Math.round(n)}`;
                    },
                  },
                },
              },
            }}
          />
        </div>
      )}
      <p className="text-[11px] text-gray-400 mt-2">
        Active and trialing subscriptions, on the day they renew. Past due is already in Outstanding.
        {unscheduled > 0 ? ` ${unscheduled} subscription${unscheduled === 1 ? '' : 's'} with no renewal date left out.` : ''}
      </p>
    </div>
  );
}
